/*
 * Copyright (c) 2021 Daimler AG. All rights reserved.
 * Implemented 2021 by DCCS GmbH.
 */

import React, { createRef, forwardRef, useImperativeHandle, useState } from 'react';
import { CircularProgress, Modal, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface LoadingAction {
  showLoadingPage: (visible: boolean) => void;
}

const loadingIndicatorRef = createRef<LoadingAction>();

const InternalLoadingIndicator = forwardRef((_, ref) => {
  const [count, setCount] = useState(0);
  useImperativeHandle(ref, () => ({
    showLoadingPage(visible: boolean) {
      if (visible) {
        setCount((prevState) => prevState + 1);
      } else {
        setCount((prevState) => (prevState > 0 ? prevState - 1 : 0));
      }
    },
  }));

  const { t } = useTranslation();

  return (
    <Modal
      open={count > 0}
      disablePortal
      disableEnforceFocus
      disableAutoFocus
      className={'loading-page'}
    >
      <Paper elevation={0} className={'page'}>
        <CircularProgress
          classes={{
            root: 'circular',
          }}
        />
        <Typography variant="subtitle1" className={'text'}>
          {t('loading')}
        </Typography>
      </Paper>
    </Modal>
  );
});

/**
 * Loading indicator that disabled everything and brings it to background then showing circular
 */
export const LoadingIndicator = () => {
  return <InternalLoadingIndicator ref={loadingIndicatorRef} />;
};

/**
 * Implementation of loader as requested by the RestClient.
 */
export class LoadingIndicatorAdapter {
  static hide(): void {
    loadingIndicatorRef.current?.showLoadingPage(false);
  }

  static show(): void {
    loadingIndicatorRef.current?.showLoadingPage(true);
  }
}
